import { readOiAltitudeBand } from '@/lib/mqtt/oiAltitude';
import type {
  MissionProgress,
  OiGeoJsonPolygon,
  OiLiveEnvelope,
  OperationalIntentReference,
} from '@/lib/mqtt/schemas/oi';
import { rtmStatusFromCode } from '@/lib/oi/rtmStatus';
/**
 * v1 `flightplan/<UAS_ID>` adapter.
 *
 * Maps the AirMarket flightplan payload (fixture in v1's
 * `src/data/flightplan.js`) onto the v2 `OiLiveEnvelope`. The big win is
 * the **polygon** — v1 ships the real OI volume here as `flight_volume`
 * (an array of GeoJSON-style rings), which in v2 drives the map OI
 * overlay. Without this stream, live mode falls back to a synthetic
 * bounding box around each drone.
 *
 * The flightplan topic publishes at low cadence (~1 msg / 7 s observed
 * 2026-05-15). Treated as a snapshot of the static OI shape; live
 * conformance / mission progress would come from a different topic that
 * v1 doesn't expose, so those fields stay null.
 *
 * Verified payload shape (excerpt from broker sniff + v1 fixture):
 *   {
 *     UAS_ID, callsign, sn, drone_type, model,
 *     flight_guid, mission_guid,
 *     legal_profile, main_flight_type,
 *     flight_date_utc, duration_seconds, flight_time,
 *     upcoming_flight,        // 1 = future, 0 = current
 *     personnel: [ { full_name, role } ],
 *     flight_volume: [ [ [lng,lat], ... ], ... ],  // GeoJSON-ring style
 *     waypoints: [ [ [lat,lng,alt_m], ... ], ... ],
 *     ...
 *   }
 */
import { z } from 'zod';

/** Subset of v1 flightplan we rely on. Everything else is tolerated.
 *  Sniff 2026-05-26 against staging confirmed the live broker actually
 *  ships only these fields: `UAS_ID`, `UAS_ID_TYPE`, `declaration_id`,
 *  `sn`, `timestamp`, `flight_volume`, `waypoints`, `operational_volume`.
 *  All the v1-fixture fields (callsign, flight_guid, legal_profile,
 *  duration_seconds, personnel, etc.) are accepted but absent in
 *  practice — keep them in the schema so a richer publisher is
 *  forwards-compatible. */
export const V1FlightplanPayload = z
  .object({
    UAS_ID: z.string(),
    declaration_id: z.string().nullish(), // canonical OI id on broker
    sn: z.string().nullish(),
    callsign: z.string().nullish(),
    flight_guid: z.string().nullish(),
    mission_guid: z.string().nullish(),
    legal_profile: z.string().nullish(),
    main_flight_type: z.string().nullish(),
    flight_time: z.string().nullish(),
    flight_date_utc: z.string().nullish(),
    duration_seconds: z.number().nullish(),
    timestamp: z.string().nullish(),
    timestamp_utc: z.string().nullish(),
    upcoming_flight: z.number().int().nullish(),
    personnel: z
      .array(
        z.object({ full_name: z.string().nullish(), role: z.string().nullish() }).passthrough(),
      )
      .nullish(),
    flight_volume: z.unknown().nullish(),
    waypoints: z.unknown().nullish(),
    max_altitude: z.number().nullish(),
    /** Added to `oi/declaration` on 2026-08-27. Absent on the legacy
     *  `flightplan/` topic, which routes through this same mapper — hence
     *  `.nullish()` on the block and on every field inside it. */
    operational_intent_reference: z
      .object({
        id: z.string().nullish(),
        subscription_id: z.string().nullish(),
        manager: z.string().nullish(),
        uss_base_url: z.string().nullish(),
        uss_availability: z.string().nullish(),
        state: z.string().nullish(),
        version: z.string().nullish(),
        ovn: z.string().nullish(),
        time_start: z.string().nullish(),
        time_end: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    rtm_state: z.number().nullish(),
    rtm_is_approved: z.boolean().nullish(),
    time_amend_enabled: z.boolean().nullish(),
    time_amend_trigger_s: z.number().nullish(),
    time_amend_extension_s: z.number().nullish(),
    time_added_total_s: z.number().nullish(),
    time_added_max_s: z.number().nullish(),
    /** Authoritative source for polygon + schedule in the current broker
     *  payload. GeoJSON FeatureCollection; the first feature's geometry
     *  is the OV polygon, its `properties.time_start` / `time_end` give
     *  the OV window. */
    operational_volume: z
      .object({
        type: z.literal('FeatureCollection').nullish(),
        features: z
          .array(
            z
              .object({
                type: z.literal('Feature').nullish(),
                geometry: z
                  .object({
                    type: z.string().nullish(),
                    coordinates: z.unknown().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                properties: z
                  .object({
                    time_start: z.string().nullish(),
                    time_end: z.string().nullish(),
                    /** Declared volume band. Present on `telemetry`'s copy of
                     *  `operational_volume`, absent on `oi/declaration`'s
                     *  (verified live 2026-08-24). Read via
                     *  `lib/mqtt/oiAltitude.ts`, which is the only thing
                     *  allowed to fill the OI's Alt Lower / Alt Upper. */
                    min_altitude: z
                      .object({ meters: z.number().nullish(), datum: z.string().nullish() })
                      .passthrough()
                      .nullish(),
                    max_altitude: z
                      .object({ meters: z.number().nullish(), datum: z.string().nullish() })
                      .passthrough()
                      .nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
          )
          .nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
export type V1FlightplanPayload = z.infer<typeof V1FlightplanPayload>;

/** Convert v1 `flight_volume` (array of rings) into a GeoJSON Polygon.
 *  v1 publishes [[lng, lat], ...] tuples — same order as GeoJSON, no
 *  flip needed. Filters obviously malformed rings (< 3 points) and
 *  closes them if not already closed (GeoJSON spec requires
 *  first == last point per ring). Returns null when no usable ring
 *  is present. */
function flightVolumeToPolygon(raw: unknown): OiGeoJsonPolygon | null {
  if (!Array.isArray(raw)) return null;

  // v1 sometimes ships a SINGLE ring as a flat [[lng,lat], ...] array
  // (no outer wrap), sometimes wraps multiple rings as [[[lng,lat]...], ...].
  // Detect by inspecting the inner depth.
  const looksLikePoints =
    raw.length > 0 &&
    Array.isArray(raw[0]) &&
    raw[0].length >= 2 &&
    typeof raw[0][0] === 'number' &&
    typeof raw[0][1] === 'number';
  const ringsRaw: unknown[] = looksLikePoints ? [raw] : raw;

  const rings: [number, number][][] = [];
  for (const ringRaw of ringsRaw) {
    if (!Array.isArray(ringRaw)) continue;
    const pts: [number, number][] = [];
    for (const p of ringRaw) {
      if (!Array.isArray(p) || p.length < 2) continue;
      const lng = Number(p[0]);
      const lat = Number(p[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
      pts.push([lng, lat]);
    }
    if (pts.length < 3) continue;
    // Close the ring if not already closed.
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) pts.push([first[0], first[1]]);
    rings.push(pts);
  }
  if (rings.length === 0) return null;
  return { type: 'Polygon', coordinates: rings };
}

/** Walk waypoints and return min/max altitudes (metres). v1 ships
 *  waypoint tuples as [lat, lng, alt_m] — the third slot is altitude.
 *  Handles both flat `[[lat,lng,alt], ...]` and nested-ring
 *  `[[[lat,lng,alt], ...], ...]` shapes. Returns null bounds when no
 *  numeric altitudes are present. */
function waypointAltitudeBand(raw: unknown): { floorM: number | null; ceilingM: number | null } {
  if (!Array.isArray(raw)) return { floorM: null, ceilingM: null };
  let floor = Number.POSITIVE_INFINITY;
  let ceiling = Number.NEGATIVE_INFINITY;
  const walk = (arr: unknown[]) => {
    for (const wp of arr) {
      if (!Array.isArray(wp)) continue;
      if (Array.isArray(wp[0])) {
        walk(wp as unknown[]);
        continue;
      }
      const alt = Number(wp[2]);
      if (!Number.isFinite(alt)) continue;
      if (alt < floor) floor = alt;
      if (alt > ceiling) ceiling = alt;
    }
  };
  walk(raw);
  if (!Number.isFinite(floor) || !Number.isFinite(ceiling)) {
    return { floorM: null, ceilingM: null };
  }
  return { floorM: floor, ceilingM: ceiling };
}

/** Count waypoints across all rings. Falls back to 0 if `waypoints` is
 *  missing or malformed. */
function countWaypoints(raw: unknown): number {
  if (!Array.isArray(raw)) return 0;
  // v1 waypoints can be either a flat list [[lat,lng,alt], ...] or
  // nested rings [[[lat,lng,alt], ...], ...]. Handle both.
  const first = raw[0];
  if (Array.isArray(first) && Array.isArray(first[0])) {
    // nested
    let total = 0;
    for (const ring of raw) if (Array.isArray(ring)) total += ring.length;
    return total;
  }
  return raw.length;
}

/** Pick the pilot from the personnel array. v1's role field is
 *  free-text — "Pilot" / "Pilot in Command" / "PIC" / etc. — so match
 *  case-insensitively on a few common patterns. */
function findPilot(personnel: V1FlightplanPayload['personnel']): string | null {
  if (!personnel) return null;
  for (const p of personnel) {
    if (!p.full_name || !p.role) continue;
    const role = p.role.toLowerCase();
    if (role.includes('pilot') || role === 'pic') return p.full_name;
  }
  // Fall back to first named member.
  for (const p of personnel) {
    if (p.full_name) return p.full_name;
  }
  return null;
}

/** Normalise any ISO date variant to UTC "Z" form. Matches the
 *  networkid mapper's helper — keeps Zod's `.datetime({offset:true})`
 *  validation happy. */
function normaliseIsoUtc(s: string | null | undefined): string | null {
  if (!s) return null;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  return new Date(t).toISOString();
}

/** Map v1 flightplan → partial v2 OiLiveEnvelope. The "partial" here is
 *  whichever fields v1's flightplan actually carries — the source merges
 *  this into its per-drone OI snapshot rather than overwriting. */
export interface FlightplanUpdate {
  drone_id: string;
  partial: Partial<OiLiveEnvelope>;
}

/** Pull the polygon + schedule from the broker's `operational_volume`
 *  FeatureCollection (the authoritative source on staging today). */
function readOperationalVolume(ov: V1FlightplanPayload['operational_volume']): {
  polygon: OiGeoJsonPolygon | null;
  startUtc: string | null;
  endUtc: string | null;
} {
  if (!ov?.features?.length) return { polygon: null, startUtc: null, endUtc: null };
  const f = ov.features[0]!;
  let polygon: OiGeoJsonPolygon | null = null;
  if (f.geometry && f.geometry.type === 'Polygon') {
    polygon = flightVolumeToPolygon(f.geometry.coordinates);
  }
  const startUtc = normaliseIsoUtc(f.properties?.time_start ?? null);
  const endUtc = normaliseIsoUtc(f.properties?.time_end ?? null);
  return { polygon, startUtc, endUtc };
}

export function flightplanToOiUpdate(raw: unknown): FlightplanUpdate | null {
  const parsed = V1FlightplanPayload.safeParse(raw);
  if (!parsed.success) return null;
  const v1 = parsed.data;

  // Three sources for the polygon, in order of canonicity per the May 2026
  // broker sniff: operational_volume.features[0].geometry > flight_volume.
  const ov = readOperationalVolume(v1.operational_volume);
  const polygon = ov.polygon ?? flightVolumeToPolygon(v1.flight_volume);

  // The authority's operational-intent record, when published. Its own
  // time_start/time_end are the window the OI was actually accepted for, so
  // they lead the `operational_volume` properties — the two agree today, but
  // an amendment updates the reference first.
  const oirRaw = v1.operational_intent_reference;
  const oir: OperationalIntentReference | null = oirRaw
    ? {
        id: oirRaw.id ?? null,
        subscription_id: oirRaw.subscription_id ?? null,
        manager: oirRaw.manager ?? null,
        uss_base_url: oirRaw.uss_base_url ?? null,
        uss_availability: oirRaw.uss_availability ?? null,
        state: oirRaw.state ?? null,
        version: oirRaw.version ?? null,
        ovn: oirRaw.ovn ?? null,
        // Published with a `+00:00` offset here and a `Z` suffix on
        // `operational_volume` — normalised so both read identically.
        time_start: normaliseIsoUtc(oirRaw.time_start),
        time_end: normaliseIsoUtc(oirRaw.time_end),
      }
    : null;

  // Schedule: prefer the operational intent reference, then
  // operational_volume properties, then legacy flight_date_utc +
  // duration_seconds (v1 fixture shape, not currently published on staging
  // but kept for forwards compat).
  let startUtc = oir?.time_start ?? ov.startUtc;
  let endUtc = oir?.time_end ?? ov.endUtc;
  if (!startUtc) startUtc = normaliseIsoUtc(v1.flight_date_utc);
  if (!endUtc && startUtc && typeof v1.duration_seconds === 'number' && v1.duration_seconds > 0) {
    endUtc = new Date(Date.parse(startUtc) + v1.duration_seconds * 1000).toISOString();
  }

  // Status is the RTM authority's `rtm_state`, and only that (ENG-213) — no
  // schedule inference, no `oir.state` string mapping. A message without
  // rtm_state emits no status, so it can't overwrite the last published state
  // (the legacy topic shares this mapper); an unmapped code reads 'Unknown'.
  const statusPatch =
    typeof v1.rtm_state === 'number' ? { status: rtmStatusFromCode(v1.rtm_state) } : {};

  // OI volume band. Read from `operational_volume`'s declared altitudes (or
  // `alt_*_wgs84_m` when the payload carries them) — never from waypoints,
  // which are AGL and feed `agl_ceiling_ft` below. See lib/mqtt/oiAltitude.ts.
  const band = readOiAltitudeBand(raw);

  const waypointsTotal = countWaypoints(v1.waypoints);
  // We don't know live progress from this topic — leave the whole mission
  // block null rather than emit fake "0/N" numbers that would shrink as
  // soon as a real progress source lands.
  const mission: MissionProgress | null =
    waypointsTotal > 0
      ? {
          waypoints_total: waypointsTotal,
          waypoints_done: 0,
          next_wp_id: null,
          dist_to_next_km: null,
        }
      : null;

  // AGL ceiling — this IS what waypoint altitudes are (verified 2026-08-24:
  // waypoint 700 m + terrain 468.79 m = the volume's 1168.79 m W84 ceiling),
  // so they stay here and only here.
  const { ceilingM: waypointCeilingM } = waypointAltitudeBand(v1.waypoints);
  const aglCeilingFt = waypointCeilingM != null ? Math.round(waypointCeilingM * 3.28084) : null;

  // OI id: prefer broker `declaration_id` (current staging shape), then
  // legacy flight_guid / mission_guid, finally fall back to UAS_ID so
  // we always emit a stable key.
  const oi_id =
    v1.declaration_id?.trim() || v1.flight_guid?.trim() || v1.mission_guid?.trim() || v1.UAS_ID;

  const partial: Partial<OiLiveEnvelope> = {
    drone_id: v1.UAS_ID,
    oi_id,
    // The RTM operational intent id when published, else the declaration id.
    // These are different identifiers for different records; the reference's
    // is the one a USS can be queried with, so it leads where it exists.
    oi_ref_id: oir?.id ?? v1.declaration_id?.trim() ?? v1.flight_guid?.trim() ?? null,
    ...statusPatch,
    legal_rule: v1.legal_profile?.trim() ?? null,
    op_type: v1.main_flight_type?.trim() ?? null,
    // The authority's approval flag, when published. Rendered as the DSS
    // submission status; '—' before 2026-08-27 because nothing carried it.
    ops_approval:
      typeof v1.rtm_is_approved === 'boolean'
        ? v1.rtm_is_approved
          ? 'Approved'
          : 'Not approved'
        : null,
    oir,
    rtm_state: typeof v1.rtm_state === 'number' ? v1.rtm_state : null,
    rtm_is_approved: typeof v1.rtm_is_approved === 'boolean' ? v1.rtm_is_approved : null,
    time_amend_enabled: typeof v1.time_amend_enabled === 'boolean' ? v1.time_amend_enabled : null,
    time_amend_trigger_s: typeof v1.time_amend_trigger_s === 'number' ? v1.time_amend_trigger_s : null,
    time_amend_extension_s:
      typeof v1.time_amend_extension_s === 'number' ? v1.time_amend_extension_s : null,
    time_added_total_s: typeof v1.time_added_total_s === 'number' ? v1.time_added_total_s : null,
    time_added_max_s: typeof v1.time_added_max_s === 'number' ? v1.time_added_max_s : null,
    pilot_name: findPilot(v1.personnel ?? []),
    start_utc: startUtc,
    end_utc: endUtc,
    polygon,
    mission,
    agl_ceiling_ft: aglCeilingFt,
    altitude_floor_m: band.floorM,
    altitude_ceiling_m: band.ceilingM,
    altitude_datum: band.datum,
    // Don't touch rth / subscribed_services here.
  };
  return { drone_id: v1.UAS_ID, partial };
}
